import React, { useState, useEffect } from "react";
import { NextPage } from "next";
import Head from "next/head";

import wp from "src/lib/wp";
import { parseHTML } from "src/lib/functions";

import { socialMetadata } from "src/components/layout/Head";
import Link from "next/link";
import { IPageProps } from "../_app";
import SignupCallout from "src/components/sales/SignupCallout";

// Constants
const PAGE_ID = 367;

type IAboutPageProps = {
  page?: any;
};

const About: NextPage<IAboutPageProps & IPageProps> = ({ page, signup }) => {
  const [width, setWidth] = useState<number>(0);

  const calculatePageWidth = () => setWidth(window.innerWidth);

  useEffect(() => {
    calculatePageWidth();
    window.addEventListener("resize", calculatePageWidth);
  }, []);

  const lg = width > 992;

  return (
    <>
      <Head>
        {socialMetadata({
          title: "About - HASH",
          url: "https://hash.ai/about",
        })}
      </Head>
      <div className="about-page">
        <div className="mainsite-header">
          <div className="container">
            <h1 className="heading-1">
              {parseHTML(page?.acf.heading ?? "About")}
            </h1>
            <p className="large-text">{parseHTML(page?.acf.sub_heading)}</p>
          </div>
        </div>
        <div className="container about-content">
          {!page ? (
            <div className="large-text" style={{ marginBottom: "60px" }}>
              We can't load this page right now. Please check back again a
              little later.
            </div>
          ) : (
            <div className="row">
              <div className="col-lg-6 left">
                <span>
                  <h3 style={{ marginTop: "0" }}>
                    {parseHTML(page.acf.left_section_1_heading)}
                  </h3>
                  <p>{parseHTML(page.acf.left_section_1_body)}</p>
                </span>
                {!lg && <Platform page={page} lg={lg} />}
                <span>
                  <h3>{parseHTML(page.acf.left_section_2_heading)}</h3>
                  <p>{parseHTML(page.acf.left_section_2_body)}</p>
                </span>
                <span>
                  <h3>{parseHTML(page.acf.left_section_3_heading)}</h3>
                  <p>{parseHTML(page.acf.left_section_3_body)}</p>
                </span>
              </div>

              {lg && (
                <div className="col-lg-6 right">
                  <Platform page={page} lg={lg} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SignupCallout signup={signup} />
    </>
  );
};

About.getInitialProps = async () => {
  try {
    let page;
    page = await wp.pages().id(PAGE_ID);
    return { page };
  } catch (err) {
    return { page: undefined } as any; // todo; fix this hack
  }
};

const Platform: React.FC<{ lg: boolean; page: any }> = ({ lg, page }) => {
  return (
    <>
      <h3 style={lg ? { marginTop: "0" } : undefined}>
        {parseHTML(page?.acf.right_heading)}
      </h3>
      <p style={{ marginBottom: "2em" }}>
        {parseHTML(page?.acf.right_top_body)}
      </p>
      <Link href="/platform/hash">
        <a className="platform-item link">
          <img src="/assets/package-variant.svg" />
          <span>
            <p className="title">HASH</p>
            <p>All-in-one platform for block-based application development, and data/type management.</p>
          </span>
        </a>
      </Link>
      <Link href="/platform/core">
        <a className="platform-item link">
          <img
            style={{ marginTop: "0.15rem", left: "-7px", marginRight: "7px" }}
            src="/assets/H-CORE.svg"
          />
          <span>
            <p className="title">HASH Core</p>
            <p>Our in-browser environment for quickly creating, visualizing and exploring simulations of complex systems.</p>
          </span>
        </a>
      </Link>
      <Link href="/platform/engine">
        <a className="platform-item link">
          <img
            style={{ marginTop: "0.15rem", marginRight: "19px" }}
            src="/assets/rocket.svg"
          />
          <span>
            <p className="title">HASH Engine</p>
            <p>The high-performance simulation engine powering HASH Core, available for use on your own machine.</p>
          </span>
        </a>
      </Link>
      <p>{parseHTML(page.acf.right_bottom_body)}</p>
    </>
  );
};

export default About;
